import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

export interface ScriptConfig {
  id: string;
  src: string;
  rootElementId?: string;
  cleanupElementIds?: string[];
  cleanupSelectors?: string[];
  cleanupGlobalKeys?: string[];
  /**
   * Key/value pairs set as data-* attributes on the <script> element.
   * The script can read them via document.currentScript.dataset.
   */
  dataset?: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class ScriptLoaderService {
  private readonly document = inject(DOCUMENT);
  private loadedScripts = new Map<string, HTMLScriptElement>();

  private get window(): Window | null {
    return this.document.defaultView;
  }

  load(config: ScriptConfig, enabled: boolean): boolean {
    if (!enabled) return false;
    if (this.document.getElementById(config.id)) return false;

    if (config.rootElementId && !this.document.getElementById(config.rootElementId)) {
      const root = this.document.createElement('div');
      root.id = config.rootElementId;
      this.document.body.appendChild(root);
    }

    const script = this.document.createElement('script');
    script.id = config.id;
    script.src = config.src;
    script.async = true;

    for (const [key, value] of Object.entries(config.dataset ?? {})) {
      script.dataset[key] = value;
    }

    this.document.body.appendChild(script);
    this.loadedScripts.set(config.id, script);

    return true;
  }

  unload(config: ScriptConfig): void {
    const script = this.loadedScripts.get(config.id);
    if (script) {
      script.remove();
      this.loadedScripts.delete(config.id);
    }

    const idsToRemove = [...(config.cleanupElementIds ?? [])];
    if (config.rootElementId) {
      idsToRemove.push(config.rootElementId);
    }

    for (const id of idsToRemove) {
      this.document.querySelectorAll(`#${id}`).forEach(el => el.remove());
    }

    for (const selector of config.cleanupSelectors ?? []) {
      this.document.querySelectorAll(selector).forEach(el => el.remove());
    }

    const win = this.window as any;
    if (win) {
      for (const key of config.cleanupGlobalKeys ?? []) {
        delete win[key];
      }
    }
  }

  isLoaded(scriptId: string): boolean {
    return this.loadedScripts.has(scriptId);
  }
}
